import { useEffect, useState } from 'react';
import type { NavigationMapInfo, WaypointMap } from '@shared/lib/api/navigation';
import { apiFetch, apiGet } from '@/lib/api';
import { useRobotStore } from '@/stores/useRobotStore';

export function useNavigationData() {
  const [mapInfo, setMapInfo] = useState<NavigationMapInfo | null>(null);
  const [waypoints, setWaypoints] = useState<WaypointMap>({});
  const [mapUrl, setMapUrl] = useState('');
  const [loading, setLoading] = useState(true);

  const mapRevision = useRobotStore((s) => s.mapRevision);
  const robotOnline = useRobotStore((s) => s.robotOnline);

  useEffect(() => {
    if (!robotOnline) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);

    Promise.all([
      apiGet<NavigationMapInfo>('/data/navigation/map_info'),
      apiGet<{ waypoints?: WaypointMap }>('/data/navigation/waypoints'),
    ])
      .then(([info, wps]) => {
        if (cancelled) return;
        setMapInfo(info);
        setWaypoints(wps.waypoints ?? {});
        setLoading(false);
      })
      .catch(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [robotOnline]);

  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        const response = await apiFetch(`/data/navigation/map?rev=${mapRevision}`);
        const dataUrl = await blobToDataUrl(await response.blob());
        if (!cancelled) setMapUrl(dataUrl);
      } catch {
        if (!cancelled) setMapUrl('');
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [mapRevision, robotOnline]);

  return { mapInfo, waypoints, mapUrl, loading };
}

function blobToDataUrl(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(typeof reader.result === 'string' ? reader.result : '');
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(blob);
  });
}
